import { useIntl } from '@atlassian/jira-intl';
import { TRIGGER_POINT_KEY_BOARD_VIEW } from '@atlassian/jira-issue-archival-modal/src/constants.tsx';
import { useArchiveIssueModalActions } from '@atlassian/jira-issue-archival-modal/src/controllers/main.tsx';
import { useIsArchived } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { REFRESH_SOURCE_ARCHIVE_ISSUE } from '../../../../../../../../model/constants.tsx';
import { CAN_ARCHIVE_ISSUE } from '../../../../../../../../model/permission/permission-types.tsx';
import { workRefreshData } from '../../../../../../../../state/actions/work/index.tsx';
import { useBoardSelector, useBoardDispatch } from '../../../../../../../../state/index.tsx';
import {
	getIssueByKey,
	getIssueTypeLevelForBoardIssue,
} from '../../../../../../../../state/selectors/issue/issue-selectors.tsx';
import type { Issue } from '../../../../../../../../model/issue/issue-types.tsx';
import { getPermissionsSelector } from '../../../../../../../../state/selectors/board/board-permissions-selectors.tsx';
import { ARCHIVE_ISSUE_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import type { ContentProps, Props } from './types.tsx';

export const useArchiveIssueContent = ({
	isArchiveEnabled,
	onArchiveIssue,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	if (!isArchiveEnabled) return [];

	return [
		{
			type: MenuNodeType.Item,
			label: formatMessage(messages.archive),
			id: ARCHIVE_ISSUE_MENU_ID,
			onClick: onArchiveIssue,
		},
	];
};

export const useArchiveIssue = ({ issueKey, issueId, isMultiSelectMode }: Props): MenuNode[] => {
	const isArchived = useIsArchived();
	const issueIdArg = issueId.toString();
	const hierarchyLevel = useBoardSelector(getIssueTypeLevelForBoardIssue)(issueId);
	const isSubtask = isSubtaskHierarchyLevel(hierarchyLevel);
	const canArchiveIssue = useBoardSelector(getPermissionsSelector)?.[CAN_ARCHIVE_ISSUE];
	const edition = useAppEditions();
	const dispatch = useBoardDispatch();
	const isPremium = edition?.software === PREMIUM_EDITION;

	const issue = useBoardSelector((state): Issue | undefined => {
		return getIssueByKey(state, issueKey);
	});

	const canArchive = !isArchived && (canArchiveIssue ?? false) && !isSubtask && isPremium;
	const { openArchiveIssueModal } = useArchiveIssueModalActions();
	const onIssueArchiveSuccess = () => {
		dispatch(workRefreshData(REFRESH_SOURCE_ARCHIVE_ISSUE));
	};

	return useArchiveIssueContent({
		isArchiveEnabled: canArchive && !isMultiSelectMode,
		onArchiveIssue: () => {
			openArchiveIssueModal({
				issueKey,
				issueId: issueIdArg,
				triggerPointKey: TRIGGER_POINT_KEY_BOARD_VIEW,
				onIssueArchiveSuccess,
				workType: issue?.typeName,
			});
		},
	});
};
